import { Link, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Utensils, Menu, X, ShoppingBag } from 'lucide-react';
import { Button } from '../ui/button';
import { useCart } from '../../contexts/CartContext';
import axios from 'axios';
import { getApiBase } from '../../lib/api';

const API = getApiBase();

function PublicHeader() {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const { itemCount } = useCart();
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    axios.get(`${API}/api/settings`).then(r => setSettings(r.data)).catch(() => {});
  }, []);

  const links = [
    { to: '/', label: 'Accueil' },
    { to: '/commander', label: 'Commander' },
    { to: '/reservation', label: 'Reserver', setting: 'reservations_enabled' },
    { to: '/devis', label: 'Devis', setting: 'quotes_enabled' },
  ];
  const visibleLinks = links.filter(link => !link.setting || settings?.[link.setting] !== false);
  const isActiveLink = (to) => {
    if (to.includes('?order=true')) {
      return location.pathname === '/menu' && location.search.includes('order=true');
    }
    return location.pathname === to && (!to.includes('?') || location.search === '');
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 glass-nav border-b transition-all ${isHome ? 'bg-[#F3F4F6]/80 border-[#1E2D3D]/10' : 'bg-[#F3F4F6]/95 border-[#E5E3DD]'}`}>
      {settings?.exceptional_closure_enabled && (
        <div className="bg-[#8C3A3A] px-4 py-2 text-center text-sm text-white">
          <span className="font-semibold">{settings.exceptional_closure_title || 'Fermeture exceptionnelle'}:</span>{' '}
          {settings.exceptional_closure_message}
        </div>
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
            <Utensils className="w-6 h-6 text-[#1E2D3D] group-hover:text-[#D4A840] transition-colors" />
            <span className="font-heading max-w-[190px] text-base sm:max-w-none sm:text-xl font-semibold text-[#1A1E24] tracking-tight leading-tight">Le Traiteur Libanais</span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {visibleLinks.map(l => (
              <Link
                key={l.to}
                to={l.to}
                className={`text-sm font-medium transition-colors tracking-wide ${
                  settings?.exceptional_closure_enabled && l.label === 'Commander'
                    ? 'pointer-events-none text-[#B08B8B]'
                    : isActiveLink(l.to)
                      ? 'text-[#1E2D3D]'
                      : 'text-[#6B7280] hover:text-[#1E2D3D]'
                }`}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link to="/cart" className="relative" data-testid="cart-link">
              <ShoppingBag className="w-5 h-5 text-[#1A1E24]" />
              {itemCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#D4A840] text-[#1A1E24] text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">{itemCount}</span>
              )}
            </Link>
            <Link to="/login" className="hidden md:block">
              <Button variant="outline" size="sm" className="rounded-full border-[#5B6B3F] text-[#5B6B3F] hover:bg-[#5B6B3F] hover:text-white" data-testid="login-link">
                Connexion
              </Button>
            </Link>
            <button className="md:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#EBE8E0] bg-[#FAF8F5]/98 px-4 py-4 shadow-[0_18px_40px_rgba(26,35,50,0.08)] backdrop-blur">
          {visibleLinks.map(l => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className={`block rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                settings?.exceptional_closure_enabled && l.label === 'Commander'
                  ? 'pointer-events-none text-[#B08B8B]'
                  : isActiveLink(l.to)
                    ? 'bg-[#5B6B3F]/10 text-[#5B6B3F]'
                    : 'text-[#2A2A28] hover:bg-white'
              }`}
            >
              {l.label}
            </Link>
          ))}
          <Link to="/login" onClick={() => setOpen(false)} className="block rounded-xl px-3 py-2.5 text-sm font-medium text-[#5B6B3F] hover:bg-white">Connexion</Link>
        </div>
      )}
    </header>
  );
}

function PublicFooter() {
  return (
    <footer className="bg-[#1A2332] text-[#F3F4F6] py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Utensils className="w-5 h-5 text-[#D4A840]" />
              <span className="font-heading text-xl font-semibold">Le Traiteur Libanais</span>
            </div>
            <p className="text-sm text-[#F3F4F6]/70 leading-relaxed">
              L'authenticite libanaise au coeur de Bagnolet. Cuisine preparee avec passion et les meilleurs ingredients.
            </p>
          </div>
          <div>
            <h4 className="font-heading text-lg font-semibold mb-4 text-[#D4A840]">Horaires</h4>
            <div className="space-y-1 text-sm text-[#F3F4F6]/70">
              <p>Lundi - Samedi : 11h00 - 15h30 / 18h00 - 21h45</p>
              <p>Livraison : Lundi - Samedi 18h00 - 21h50</p>
              <p>Dimanche : Ferme</p>
            </div>
          </div>
          <div>
            <h4 className="font-heading text-lg font-semibold mb-4 text-[#D4A840]">Contact</h4>
            <div className="space-y-1 text-sm text-[#F3F4F6]/70">
              <p>16 Rue Edouard Vaillant, 93170 Bagnolet</p>
              <p>Tel : 09 50 00 05 41</p>
              <p>chezlelibanais@hotmail.fr</p>
            </div>
          </div>
        </div>
        <div className="mt-12 pt-8 border-t border-[#F3F4F6]/10 text-center text-xs text-[#F3F4F6]/40">
          2025 Le Traiteur Libanais. Tous droits reserves.
        </div>
      </div>
    </footer>
  );
}

export default function PublicLayout({ children }) {
  return (
    <div className="min-h-screen bg-[#F3F4F6]">
      <PublicHeader />
      <main className="pt-16 sm:pt-20">{children}</main>
      <PublicFooter />
    </div>
  );
}
